import ColorRushTransition from './color-rush-transition';
import type { FastLaneSection } from '../types';
import { useMemo, useEffect, useState, useRef } from 'react';
import { calculateLeft, calculateLeftOffset, calculateWidth } from './section-container.hooks';
import { useDisplayType, usePixelMap } from '../store/fast-lane-context';
import { useFastLane } from '../store/index';

export default function ColorRushSectionGroup({ entries, color, includeOffset }: { entries: FastLaneSection[], color: string, includeOffset: boolean }) {
  const pixelMap = usePixelMap();
  const display = useDisplayType();
  const isEditor = useFastLane(f => f.isEditor);
  const [domPositions, setDomPositions] = useState<{left: number, totalWidth: number} | null>(null);
  const colorRushRef = useRef<HTMLDivElement>(null);

  // Calculate positions from DOM elements
  useEffect(() => {
    const calculateDomPositions = () => {
      if (!colorRushRef.current) {
        return;
      }

      // Find the specific fast-lane-container that contains this ColorRushSectionGroup
      const fastLaneContainer = colorRushRef.current.closest('.fast-lane-container');
      if (!fastLaneContainer) {
        console.warn('fast-lane-container not found for this ColorRushSectionGroup');
        return;
      }

      // Find all section-container elements within this specific fast-lane-container
      const sectionContainers = fastLaneContainer.querySelectorAll('.section-container');
      
      let minLeft = includeOffset ? 0 : Infinity;
      let maxRight = 0;

      // Match section-container elements by section.index
      entries.forEach((entry, index) => {
        const sectionElement = Array.from(sectionContainers).find(el => {
          const className = el.className;
          return className.includes(`section-container-${entry.index}`) && !className.includes('empty');
        });

        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const containerRect = fastLaneContainer.getBoundingClientRect();
          
          // Calculate relative position within the container
          const relativeLeft = rect.left - containerRect.left;
          
          // Calculate width using the calculateWidth method
          const isLastInGroup = index === (entries.length - 1);
          const calculatedWidth = calculateWidth(entry, pixelMap, display, isLastInGroup);
          
          minLeft = Math.min(minLeft, relativeLeft);
          maxRight = Math.max(maxRight, relativeLeft + calculatedWidth);
        }
      });

      const totalWidth = maxRight - minLeft;

      setDomPositions({
        left: minLeft,
        totalWidth: totalWidth
      });
    };

    // Calculate positions after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateDomPositions, 100);
    
    // Also recalculate on window resize
    window.addEventListener('resize', calculateDomPositions);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateDomPositions);
    };
  }, [entries, pixelMap, display, includeOffset]);

  const {left, totalWidth} = useMemo(() => {
    // Use DOM positions if available, otherwise fall back to calculated positions
    if (domPositions) {
      console.log('domPositions used', domPositions);
      return domPositions;
    }

    console.log('fallback calculation used');

    // todo: remove this fallback calculation after we have the DOM positions tested
    // Fallback calculation (original logic)
    let sectionLeft = includeOffset ? 0 : Infinity;
    let totalWidth = 0;

    if (includeOffset) {
      totalWidth = calculateLeft({section: entries[0], display, pixelMap, isEditor});
    }

    entries.forEach((entry, index) => {
      const left = calculateLeft({
        section: entry,
        display,
        pixelMap,
        isEditor,
      });

      const isLastInGroup = index === (entries.length - 1);

      const width = calculateWidth(entry, pixelMap, display, isLastInGroup);

      sectionLeft = Math.min(sectionLeft, left);
      totalWidth = totalWidth + width
    });

    return {
      left: sectionLeft,
      totalWidth,
    };
  }, [entries, display, pixelMap, isEditor, domPositions]);

  return (
    <ColorRushTransition
      ref={colorRushRef}
      style={{
        position: 'absolute',
        left,
        height: '100%',
        width: totalWidth,
        background: color,
      }}
      farRight={totalWidth}
    />
  );
}
